import type { Message } from "@/components/types";
import {
  buildReviewPlanTerms,
  type PracticeRecommendation,
} from "./chat-loop-helpers";

export type PracticeShortcutId =
  | "speaking"
  | "listening"
  | "writing"
  | "reading"
  | "vocabulary"
  | "grammar"
  | "review";

export interface PracticeShortcutOption {
  id: PracticeShortcutId;
  icon: string;
  label: string;
  description: string;
  recommendation?: PracticeRecommendation | null;
  context: string;
}

export interface BuildPracticeShortcutsOptions {
  tSafe: (
    key: string,
    fallback: string,
    values?: Record<string, any>,
  ) => string;
  latestAssistant: Message | null;
  fallbackUserInput: string;
}

const ensureContext = (value: string | null | undefined, fallback: string) => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const formatVocabularyLines = (message: Message | null, fallback: string) => {
  const vocabDetails = Array.isArray(message?.vocabDetails)
    ? message?.vocabDetails
    : [];
  if (vocabDetails.length > 0) {
    return vocabDetails
      .map((detail) => {
        const term = typeof detail?.term === "string" ? detail.term : "";
        if (!term.trim()) return null;
        const note = detail?.note ? ` — ${detail.note}` : "";
        return `- ${term}${note}`;
      })
      .filter(Boolean)
      .join("\n");
  }

  const vocabList = Array.isArray(message?.vocabList) ? message?.vocabList : [];
  if (vocabList.length > 0) {
    return vocabList
      .filter((term) => typeof term === "string" && term.trim().length > 0)
      .map((term) => `- ${term}`)
      .join("\n");
  }
  return fallback;
};

const collectGrammarNotes = (message: Message | null) => {
  const entries = Array.isArray(message?.corrections)
    ? message.corrections
    : [];
  return entries
    .filter((entry) => {
      const type =
        typeof entry?.type === "string" ? entry.type.toLowerCase() : "";
      return type.includes("grammar");
    })
    .map((entry) => {
      const issue = typeof entry?.issue === "string" ? entry.issue.trim() : "";
      const suggestion =
        typeof entry?.suggestion === "string" ? entry.suggestion.trim() : "";
      if (!issue && !suggestion) return null;
      const expl =
        typeof entry?.explanation === "string" ? entry.explanation.trim() : "";
      const base =
        issue && suggestion ? `${issue} → ${suggestion}` : suggestion || issue;
      return expl ? `${base} (${expl})` : base;
    })
    .filter(Boolean) as string[];
};

export function buildPracticeShortcuts(
  options: BuildPracticeShortcutsOptions,
): PracticeShortcutOption[] {
  const { tSafe, latestAssistant, fallbackUserInput } = options;
  const translate = (
    key: string,
    fallback: string,
    values?: Record<string, any>,
  ) => tSafe(key, fallback, values);

  const trimmedInput = fallbackUserInput.trim();
  const latestContent = latestAssistant?.content?.trim() ?? "";
  const baseContext = latestContent || trimmedInput;

  const speakingContext = ensureContext(
    latestAssistant?.practiceTask?.prompt ??
      latestAssistant?.nextPracticeHint ??
      latestContent,
    translate(
      "practice.speaking_default",
      "Generate a speaking drill inspired by the latest topic.",
    ),
  );

  const listeningContext = ensureContext(
    latestContent,
    translate(
      "practice.listening_default",
      "Create a listening passage with three comprehension checks based on the latest reply.",
    ),
  );

  const writingContext = ensureContext(
    `${latestContent}\n${translate("practice.writing_default", "Write ~80 words on the latest topic and include detailed correction prompts.")}`,
    translate(
      "practice.writing_fallback",
      "Write a short paragraph about the current topic and highlight grammar or vocabulary focus areas.",
    ),
  );

  const readingContext = ensureContext(
    latestContent,
    translate(
      "practice.reading_default",
      "Generate a 120-150 word reading text followed by three scaffolded comprehension questions.",
    ),
  );

  const vocabularyLines = formatVocabularyLines(
    latestAssistant,
    translate(
      "practice.vocab_default",
      "List vocabulary to reinforce and craft fill-in or usage prompts.",
    ),
  );
  const vocabularyContext =
    `${translate("practice.vocab_prompt", "Create drills for the following terms:")}\n${vocabularyLines}`.trim();

  const grammarNotes = collectGrammarNotes(latestAssistant);
  const grammarContext = grammarNotes.length
    ? `${translate("practice.grammar_prompt", "Focus on these grammar issues and design targeted practice:")}\n- ${grammarNotes.join(
        "\n- ",
      )}`
    : translate(
        "practice.grammar_default",
        "Design correction drills for the most common grammar slips in the latest exchange.",
      );

  const reviewTerms = buildReviewPlanTerms(latestAssistant, baseContext);
  const reviewContext = reviewTerms.length
    ? `${translate("practice.review_prompt", "Generate a review mini-plan for these items:")}\n${reviewTerms
        .map(
          (term) =>
            `- ${term.term}${term.definition ? `: ${term.definition}` : ""}`,
        )
        .join("\n")}`
    : translate(
        "practice.review_default",
        "Create three follow-up review tasks (covering speaking, listening, reading, writing) based on the previous topic.",
      );

  return [
    {
      id: "speaking",
      icon: "🗣️",
      label: translate("practice.shortcut_speaking", "Speaking drill"),
      description: translate(
        "practice.shortcut_speaking_desc",
        "Spin up a quick role-play or speaking drill",
      ),
      recommendation: { types: ["roleplay", "shadowing"] },
      context: speakingContext,
    },
    {
      id: "listening",
      icon: "🎧",
      label: translate("practice.shortcut_listening", "Listening focus"),
      description: translate(
        "practice.shortcut_listening_desc",
        "Produce listening content plus comprehension checks",
      ),
      recommendation: { types: ["listening"], difficulty: "balanced" },
      context: listeningContext,
    },
    {
      id: "writing",
      icon: "✍️",
      label: translate("practice.shortcut_writing", "Writing booster"),
      description: translate(
        "practice.shortcut_writing_desc",
        "Write a short piece and get feedback focus",
      ),
      recommendation: { types: ["quiz"], difficulty: "challenge" },
      context: writingContext,
    },
    {
      id: "reading",
      icon: "📖",
      label: translate("practice.shortcut_reading", "Reading builder"),
      description: translate(
        "practice.shortcut_reading_desc",
        "Produce scaffolded reading practice",
      ),
      recommendation: { types: ["shadowing", "quiz"], difficulty: "balanced" },
      context: readingContext,
    },
    {
      id: "vocabulary",
      icon: "🧠",
      label: translate("practice.shortcut_vocabulary", "Vocabulary drill"),
      description: translate(
        "practice.shortcut_vocabulary_desc",
        "Reinforce highlighted vocabulary from the last turn",
      ),
      recommendation: { types: ["quiz"], difficulty: "gentle" },
      context: vocabularyContext,
    },
    {
      id: "grammar",
      icon: "⚙️",
      label: translate("practice.shortcut_grammar", "Grammar focus"),
      description: translate(
        "practice.shortcut_grammar_desc",
        "Design practice around recent corrections",
      ),
      recommendation: { types: ["quiz"], difficulty: "challenge" },
      context: grammarContext,
    },
    {
      id: "review",
      icon: "🔁",
      label: translate("practice.shortcut_review", "Quick review"),
      description: translate(
        "practice.shortcut_review_desc",
        "Create a mini review plan from this chat",
      ),
      recommendation: {
        types: ["roleplay", "listening", "shadowing", "quiz"],
        difficulty: "balanced",
      },
      context: reviewContext,
    },
  ];
}
